import React, { forwardRef } from 'react';
import styles from './FormInput.module.css';

/** ...rest and the ref go to the input; className stays on the wrapper. */
export interface IFormInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  id: string;
  name: string;
  label?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number';
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  helpText?: string;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
  className?: string;
  inputClassName?: string;
  /** Rendered after the input inside a positioned wrapper; it positions itself. */
  endAdornment?: React.ReactNode;
}

const FormInput = forwardRef<HTMLInputElement, IFormInputProps>(
  (
    {
      id,
      name,
      label,
      type = 'text',
      placeholder,
      value,
      onChange,
      error,
      helpText,
      disabled = false,
      required = false,
      autoComplete,
      className,
      inputClassName,
      endAdornment,
      'aria-describedby': ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const errorId = `${id}-error`;
    const helpId = `${id}-help`;
    const showHelp = Boolean(helpText) && !error;
    const describedBy =
      [ariaDescribedBy, error && errorId, showHelp && helpId].filter(Boolean).join(' ') ||
      undefined;

    const input = (
      <input
        ref={ref}
        type={type}
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={[styles.input, inputClassName].filter(Boolean).join(' ')}
        autoComplete={autoComplete}
        aria-describedby={describedBy}
        {...rest}
      />
    );

    return (
      <div className={`${styles.formGroup} ${error ? styles.hasError : ''} ${className || ''}`}>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}
        {endAdornment ? (
          <div className={styles.inputWrapper}>
            {input}
            {endAdornment}
          </div>
        ) : (
          input
        )}
        {error && (
          <div id={errorId} className={styles.error}>
            {error}
          </div>
        )}
        {showHelp && (
          <div id={helpId} className={styles.helpText}>
            {helpText}
          </div>
        )}
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';

export default FormInput;
